import { useState } from "react";
import { AlertTriangle } from "lucide-react";
import { PendingInvite } from "@/types/mateTypes";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogTitle,
} from "@/components/ui/dialog";
import { useCancelInvite } from "@/hooks/useMates";
import { useToastHandler } from "@/hooks/useToastHandler";
import { formatRelativeTimeWithPrefix } from "@/lib/dateUtils";
import {
  Card,
  CardTitle,
  CardContent,
  CardHeader,
  CardDescription,
} from "@/components/ui/card";
import { MATE_CONFIG } from "@/config/mateConfig";

interface PendingInvitesProps {
  invites: PendingInvite[];
}

export function PendingInvites({ invites }: PendingInvitesProps) {
  const [showCancelDialog, setShowCancelDialog] = useState(false);
  const [selectedInvite, setSelectedInvite] = useState<PendingInvite | null>(
    null
  );
  const cancelMutation = useCancelInvite();
  const { handleSuccess } = useToastHandler();

  if (invites.length === 0) {
    return null;
  }

  const handleCancelClick = (invite: PendingInvite) => {
    setSelectedInvite(invite);
    setShowCancelDialog(true);
  };

  const handleConfirmCancel = async () => {
    if (!selectedInvite) return;

    try {
      await cancelMutation.mutateAsync(selectedInvite.id);
      handleSuccess("Invitation cancelled successfully.", 3000);
      setShowCancelDialog(false);
      setSelectedInvite(null);
    } catch {
      // Error handled by mutation
      setShowCancelDialog(false);
      setSelectedInvite(null);
    }
  };

  return (
    <>
      <Card className="w-full mb-10">
        <CardHeader>
          <CardTitle>Sent Invitations</CardTitle>
          <CardDescription>
            Unaccepted invitations will expire in{" "}
            {MATE_CONFIG.INVITE_EXPIRE_DAYS} days.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            {invites.map((invite) => (
              <div
                key={invite.id}
                className="min-w-0 p-4 border border-gray-200 bg-bg-base rounded-lg"
              >
                <p className="text-sm/6 font-semibold text-gray-900">
                  {invite.email}
                </p>

                <div className="mt-1 flex items-center gap-x-2 text-xs/5 text-gray-500">
                  {invite.status === "rejected" ? (
                    <div className="flex flex-col">
                      {invite.rejected_at && (
                        <span className="text-tx-error text-xs">
                          {formatRelativeTimeWithPrefix(
                            invite.rejected_at,
                            "Rejected",
                            "Just rejected"
                          )}
                        </span>
                      )}
                    </div>
                  ) : (
                    <>
                      <p className="whitespace-nowrap">
                        {formatRelativeTimeWithPrefix(
                          invite.created_at,
                          "Sent",
                          "Recently sent"
                        )}
                      </p>
                      <svg viewBox="0 0 2 2" className="size-0.5 fill-current">
                        <circle r="1" cx="1" cy="1" />
                      </svg>
                      <Button
                        variant="link"
                        size="sm"
                        onClick={() => handleCancelClick(invite)}
                        disabled={cancelMutation.isPending}
                        className="text-accent-bg-warm hover:text-accent-bg-warm/80 hover:bg-accent-bg-warm/10 p-0 h-auto text-xs font-normal"
                      >
                        Cancel
                      </Button>
                    </>
                  )}
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Cancel Confirmation Dialog */}
      <Dialog open={showCancelDialog} onOpenChange={setShowCancelDialog}>
        <DialogContent className="p-6 border-0 overflow-hidden max-w-md bg-white">
          <DialogTitle className="text-2xl font-bold flex items-center gap-2">
            <AlertTriangle className="h-5 w-5" />
            Confirm
          </DialogTitle>
          <div className="border-t border-gray-200"></div>
          <DialogDescription className=" text-tx-default text-base mb-5">
            Are you sure you want to cancel the invitation to{" "}
            <span className="font-medium">{selectedInvite?.email}</span>? They
            will no longer be able to accept this invitation.
          </DialogDescription>

          <div className="flex">
            <Button
              variant="destructive"
              size="lg"
              onClick={handleConfirmCancel}
              disabled={cancelMutation.isPending}
              className="flex-1"
            >
              {cancelMutation.isPending ? "Cancelling..." : "Cancel Invitation"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
